import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import { Box, CircularProgress, Grid } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getProjectAnalytics } from "../../analytics/api/analytics";
import { ImpressionsCard } from "../../analytics/components/ImpressionsCard";
import { RevenueCard } from "../../analytics/components/RevenueCard";
import { SalesCard } from "../../analytics/components/SalesCard";
import { useAssets } from "../../assets/contexts/AssetsContext";
import PageHeader from "../../common/components/PageHeader";
import SortableCardWrapper from "../../common/components/SortableCardWrapper";
import { toast } from "../../common/components/Toast";
import { DASHBOARD_CARDS } from "../../common/constants/cardRegistry";
import { usePreviewMode } from "../../common/contexts/PreviewModeContext";
import { useSettings } from "../../common/contexts/SettingsContext";
import { saveMyPreferences } from "../../settings/api/displayTemplates";
import { useSources } from "../../sources/api/sources";
import { AssetsList } from "../components/AssetsList";
import { LinkedSourcesSection } from "../components/LinkedSourcesSection";

// Build registry lookup for allowedChartTypes
const REGISTRY_MAP = Object.fromEntries(DASHBOARD_CARDS.map((c) => [c.key, c]));

function Dashboard() {
  const { t } = useTranslation("dashboard");
  const { assets, loading } = useAssets();
  const { sources } = useSources();
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const {
    defaultDateRange,
    dashboardCardConfig,
    updateCardChartType,
    displayTemplateLoading,
  } = useSettings();
  const { isPreviewMode, updateCardConfig } = usePreviewMode();

  useEffect(() => {
    // Wait for display template to load before fetching analytics
    // so we use the correct date range from the template
    if (displayTemplateLoading) return;

    const period_range = {
      period_start: defaultDateRange.period_start,
      period_end: defaultDateRange.period_end,
    };

    const fetchAnalytics = async () => {
      try {
        setAnalyticsLoading(true);
        const fetchedAnalytics = await getProjectAnalytics(period_range);
        setAnalytics(fetchedAnalytics);
      } catch (error) {
        toast.error(error.message || t("analytics.failedToFetch"));
      } finally {
        setAnalyticsLoading(false);
      }
    };

    fetchAnalytics();
  }, [defaultDateRange.period_start, defaultDateRange.period_end, displayTemplateLoading, t]);

  const handleChartTypeChange = useCallback(
    async (cardKey, newType) => {
      if (isPreviewMode) {
        // In preview mode, update the preview template (not user preferences)
        updateCardConfig("dashboard_config", cardKey, { chart_type: newType });
        return;
      }

      // Normal mode: update instantly in memory + persist to API
      updateCardChartType("dashboard_config", cardKey, newType);
      const updated = dashboardCardConfig.map((c) =>
        c.key === cardKey ? { ...c, chart_type: newType } : c,
      );
      try {
        await saveMyPreferences({ dashboard_config: updated });
      } catch {
        // Silently fail — local state already updated
      }
    },
    [dashboardCardConfig, updateCardChartType, isPreviewMode, updateCardConfig],
  );

  // Map card keys to their render functions
  const DASHBOARD_CARD_COMPONENTS = {
    impressions_card: ({ analytics: a, cardConfig }) => (
      <ImpressionsCard
        analytics={a}
        chartType={cardConfig.chart_type}
        allowedChartTypes={REGISTRY_MAP[cardConfig.key]?.allowedChartTypes}
        onChartTypeChange={(type) =>
          handleChartTypeChange(cardConfig.key, type)
        }
        cardKey={cardConfig.key}
        configKey="dashboard_config"
      />
    ),
    sales_card: ({ analytics: a, cardConfig }) => (
      <SalesCard
        analytics={a}
        chartType={cardConfig.chart_type}
        allowedChartTypes={REGISTRY_MAP[cardConfig.key]?.allowedChartTypes}
        onChartTypeChange={(type) =>
          handleChartTypeChange(cardConfig.key, type)
        }
        cardKey={cardConfig.key}
        configKey="dashboard_config"
      />
    ),
    revenue_card: ({ analytics: a, cardConfig }) => (
      <RevenueCard
        analytics={a}
        chartType={cardConfig.chart_type}
        allowedChartTypes={REGISTRY_MAP[cardConfig.key]?.allowedChartTypes}
        onChartTypeChange={(type) =>
          handleChartTypeChange(cardConfig.key, type)
        }
        cardKey={cardConfig.key}
        configKey="dashboard_config"
      />
    ),
    linked_sources: ({ sources: s, loading: l }) => (
      <LinkedSourcesSection sources={s} loading={l} />
    ),
    assets_list: ({ assets: a, loading: l }) => (
      <AssetsList assets={a} loading={l} />
    ),
  };

  // Cards that render inside the top analytics Grid
  const TOP_CARD_KEYS = new Set([
    "impressions_card",
    "sales_card",
    "revenue_card",
  ]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !isPreviewMode) return;

      const oldIndex = dashboardCardConfig.findIndex(
        (c) => c.key === active.id,
      );
      const newIndex = dashboardCardConfig.findIndex((c) => c.key === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = [...dashboardCardConfig];
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);

      // Update all orders in preview template
      reordered.forEach((card, i) => {
        updateCardConfig("dashboard_config", card.key, { order: i + 1 });
      });
    },
    [dashboardCardConfig, isPreviewMode, updateCardConfig],
  );

  const visibleCards = dashboardCardConfig.filter((c) => c.visible);
  const topCards = visibleCards.filter((c) => TOP_CARD_KEYS.has(c.key));
  const bottomCards = visibleCards.filter((c) => !TOP_CARD_KEYS.has(c.key));

  const renderCard = (cardConfig, extraProps = {}) => {
    const Component = DASHBOARD_CARD_COMPONENTS[cardConfig.key];
    if (!Component) return null;
    const rendered = (
      <Component
        key={cardConfig.key}
        analytics={analytics || {}}
        cardConfig={cardConfig}
        {...extraProps}
      />
    );
    if (isPreviewMode) {
      return (
        <SortableCardWrapper key={cardConfig.key} id={cardConfig.key}>
          {rendered}
        </SortableCardWrapper>
      );
    }
    return rendered;
  };

  const cardGrid = (
    <>
      {analyticsLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "120px",
          }}
        >
          <CircularProgress size={32} />
        </Box>
      ) : (
        topCards.length > 0 && (
          <Grid container spacing={{ xs: 2, md: 3 }} mb={{ xs: 2, md: 4 }}>
            {topCards.map((cardConfig) => renderCard(cardConfig))}
          </Grid>
        )
      )}

      {bottomCards.map((cardConfig) =>
        renderCard(cardConfig, { sources, assets, loading }),
      )}
    </>
  );

  return (
    <>
      <PageHeader title={t("title")} description={t("description")} />
      {isPreviewMode ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={visibleCards.map((c) => c.key)}
            strategy={rectSortingStrategy}
          >
            {cardGrid}
          </SortableContext>
        </DndContext>
      ) : (
        cardGrid
      )}
    </>
  );
}

export default Dashboard;
